import axios from 'axios';
import { elements } from './elements.js';
import { validateForm } from './validateForm.js';
import { format } from 'date-fns';
import { fieldUse, commonFields } from './fieldUse.js';
import { hiddenClass, submissionDateFormat } from '../../config.js';
import { recaptchaDataAttr, recaptchaId } from '../form.js';

const submitForm = async (fieldsToSubmit) => {
    const { contactForm, successMessage, errorMessage, submitButton, sendingAnimation } = elements;

    if (!contactForm || !successMessage || !errorMessage) return;

    try {
        // Generate reCAPTCHA v3 token
        const recaptchaInput = document.querySelector(`#${recaptchaId}`);

        if (typeof grecaptcha === 'undefined' || !recaptchaInput) return;

        const clientId = recaptchaInput.getAttribute(recaptchaDataAttr);
        const recaptchaToken = await grecaptcha.execute(clientId, { action: 'submit' });

        if (!recaptchaToken) return;

        fieldsToSubmit['g-recaptcha-response'] = recaptchaToken;

        if (!contactForm.action) return;

        // Submit the form data to Craft Wheelform
        const response = await axios.post(
            contactForm.action.value,
            fieldsToSubmit,
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            }
        );

        if (response.status === 200) {
            // Show success message
            contactForm.classList.add(hiddenClass);
            successMessage.classList.remove(hiddenClass);
            errorMessage.classList.add(hiddenClass);
            // Reset sending animation and button
            submitButton.classList.remove(hiddenClass);
            sendingAnimation.classList.add(hiddenClass);
            contactForm.reset();
        } else {
            throw new Error(`Form submission failed with status ${response.status}`);
        }
    } catch (error) {
        // Show error message
        errorMessage.classList.remove(hiddenClass);
        contactForm.classList.add(hiddenClass);
        successMessage.classList.add(hiddenClass);
        // Reset sending animation and button
        submitButton.classList.remove(hiddenClass);
        sendingAnimation.classList.add(hiddenClass);
    }
};

export const attachSubmitHandler = () => {
    const { contactForm, submitButton, enquiryType, sendingAnimation } = elements;

    // Sanity checks for required elements
    if (!contactForm || !submitButton || !enquiryType || !sendingAnimation) return;

    // Hide sending animation on first load
    sendingAnimation.classList.add(hiddenClass);
    submitButton.classList.remove(hiddenClass);

    submitButton.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const formData = new FormData(contactForm);

        const isValid = validateForm(contactForm, enquiryType.value);

        if (!isValid) return;

        // Show sending animation, hide button
        submitButton.classList.add(hiddenClass);
        sendingAnimation.classList.remove(hiddenClass);

        // Whittle down the fields to be submitted
        const selectedType = enquiryType.value;
        const fieldsToSubmit = {
            CRAFT_CSRF_TOKEN: formData.get('CRAFT_CSRF_TOKEN'),
            form_id: formData.get('form_id'),
        };

        const fieldsForEnquiryType = {
            ...commonFields,
            ...(fieldUse[selectedType]?.extraFields || {}),
        };

        Object.keys(fieldsForEnquiryType).forEach((fieldName) => {
            if (formData.has(fieldName)) {
                fieldsToSubmit[fieldName] = formData.get(fieldName);
            }
        });

        // Convert expectedStartDate to the desired format and use it for submission
        if (fieldsToSubmit.expectedStartDate) {
            const [day, month, year] = fieldsToSubmit.expectedStartDate.split('/').map(Number);
            const dateObject = new Date(year, month - 1, day);
            fieldsToSubmit.expectedStartDate = format(dateObject, submissionDateFormat);
        }

        await submitForm(fieldsToSubmit);
    });
};
